import {
  CancelEventPayload,
  CancellationReason,
  CancellationReasonPayload,
  EventCancellationDetail,
  EventCancellationListItem,
  EventCancellationsFilters,
  EventCancellationsPage,
  EventCancellationsSummary,
} from "../../models/eventCancellation";
import { API_ENDPOINTS } from "../../utils/api-service";
import { parseContentDispositionFilename } from "../../utils/functions/downloadBlob";
import { api } from "./api";

const reasonTag = (id: number) => ({ type: "cancellationReasons" as const, id });
const reasonListTag = { type: "cancellationReasons" as const, id: "LIST" };
const cancellationListTag = { type: "eventCancellations" as const, id: "LIST" };
const cancellationTag = (eventId: number | string) => ({
  type: "eventCancellations" as const,
  id: eventId,
});
const eventTag = (id: string | number) => ({
  type: "events" as const,
  id: String(id),
});
const eventListTag = { type: "events" as const, id: "LIST" };

const DEFAULT_EXPORT_FILENAME = "cancelaciones.csv";

export interface CancellationReasonsQuery {
  tenantId: string | null;
  includeInactive?: boolean;
  includeTenantCustomReasons?: boolean;
}

const unwrap = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) return response;
  const record = response as Record<string, unknown>;
  return record.data !== undefined ? record.data : response;
};

const toRows = (response: unknown): Record<string, unknown>[] => {
  const data = unwrap(response);
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (typeof data === "object" && data !== null) {
    const items = (data as Record<string, unknown>).items;
    if (Array.isArray(items)) return items as Record<string, unknown>[];
  }
  return [];
};

const toInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const toBool = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const toNullableInt = (value: unknown): number | null =>
  value === null || value === undefined || value === "" ? null : toInt(value);

const toNullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

const mapReason = (row: Record<string, unknown>): CancellationReason => ({
  id: toInt(row.id),
  tenantId: toNullableString(row.tenantId ?? row.tenant_id),
  reasonText: String(row.reasonText ?? row.reason_text ?? ""),
  userFacingMessage: toNullableString(row.userFacingMessage ?? row.user_facing_message),
  displayOrder: toInt(row.displayOrder ?? row.display_order),
  active: row.active === undefined ? true : toBool(row.active),
  usageCount: toNullableInt(row.usageCount ?? row.usage_count),
});

const mapDetail = (row: Record<string, unknown>): EventCancellationDetail => ({
  cancelledAt: String(row.cancelledAt ?? row.cancelled_at ?? ""),
  internalNote: toNullableString(row.internalNote ?? row.internal_note),
  affectedUsers: toInt(row.affectedUsers ?? row.affected_users),
  affectedCompanions: toInt(row.affectedCompanions ?? row.affected_companions),
  requiresRefund: toBool(row.requiresRefund ?? row.requires_refund),
  reasonText: String(row.reasonText ?? row.reason_text ?? ""),
  cancelledByName: String(row.cancelledByName ?? row.cancelled_by_name ?? ""),
  cancelledByRole: toNullableString(row.cancelledByRole ?? row.cancelled_by_role),
});

const mapListItem = (row: Record<string, unknown>): EventCancellationListItem => ({
  ...mapDetail(row),
  id: toInt(row.id),
  eventId: toInt(row.eventId ?? row.event_id),
  eventName: String(row.eventName ?? row.event_name ?? ""),
  tenantId: String(row.tenantId ?? row.tenant_id ?? ""),
});

const cancellationsFilterParams = (filters: EventCancellationsFilters) => ({
  ...(filters.tenantId ? { tenant_id: filters.tenantId } : {}),
  ...(filters.reasonIds?.length ? { reason_ids: filters.reasonIds.join(",") } : {}),
  from: filters.from,
  to: filters.to,
});

const cancellationsQueryParams = (filters: EventCancellationsFilters) => ({
  ...cancellationsFilterParams(filters),
  ...(filters.page ? { page: filters.page } : {}),
  ...(filters.perPage ? { per_page: filters.perPage } : {}),
});

export const eventCancellationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCancellationReasons: build.query<CancellationReason[], CancellationReasonsQuery>({
      query: ({ tenantId, includeInactive, includeTenantCustomReasons }) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASONS,
        params: {
          ...(tenantId ? { tenant_id: tenantId } : {}),
          ...(includeInactive ? { include_inactive: "true" } : {}),
          ...(includeTenantCustomReasons ? { include_tenant_custom_reasons: "true" } : {}),
        },
      }),
      transformResponse: (response: unknown) => toRows(response).map(mapReason),
      providesTags: (result) => [
        ...(result ?? []).map((reason) => reasonTag(reason.id)),
        reasonListTag,
      ],
    }),

    createCancellationReason: build.mutation<void, CancellationReasonPayload>({
      query: (body) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASONS,
        method: "POST",
        body,
      }),
      invalidatesTags: [reasonListTag],
    }),

    updateCancellationReason: build.mutation<
      void,
      { id: number } & Partial<CancellationReasonPayload>
    >({
      query: ({ id, ...body }) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASON_BY_ID(id),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [reasonTag(id), reasonListTag],
    }),

    deleteCancellationReason: build.mutation<void, number>({
      query: (id) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASON_BY_ID(id),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [reasonTag(id), reasonListTag],
    }),

    reorderCancellationReasons: build.mutation<
      void,
      { id: number; display_order: number }[]
    >({
      query: (reasons) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASONS_REORDER,
        method: "PATCH",
        body: reasons,
      }),
      invalidatesTags: [reasonListTag],
    }),

    resetCancellationReasons: build.mutation<void, void>({
      query: () => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.REASONS_RESET,
        method: "POST",
      }),
      invalidatesTags: [reasonListTag],
    }),

    cancelEvent: build.mutation<void, CancelEventPayload>({
      query: ({ eventId, reasonId, internalNote }) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.CANCEL_EVENT(eventId),
        method: "POST",
        body: {
          reason_id: reasonId,
          ...(internalNote ? { internal_note: internalNote } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        eventTag(eventId),
        eventListTag,
        cancellationTag(eventId),
        cancellationListTag,
        reasonListTag,
      ],
    }),

    getEventCancellation: build.query<EventCancellationDetail | null, number>({
      query: (eventId) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.EVENT_CANCELLATION(eventId),
      }),
      transformResponse: (response: unknown) => {
        const data = unwrap(response);
        if (typeof data !== "object" || data === null || Array.isArray(data)) {
          return null;
        }
        const row = data as Record<string, unknown>;
        return row.cancelledAt || row.cancelled_at ? mapDetail(row) : null;
      },
      providesTags: (_result, _error, eventId) => [cancellationTag(eventId)],
    }),

    getAdminEventCancellations: build.query<EventCancellationsPage, EventCancellationsFilters>({
      query: (filters) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.LIST,
        params: cancellationsQueryParams(filters),
      }),
      transformResponse: (response: unknown, _meta, filters) => {
        const data = unwrap(response) as Record<string, unknown>;
        const items = toRows(response).map(mapListItem);
        return {
          items,
          total: toInt(data?.total ?? data?.count, items.length),
          page: toInt(data?.page, filters.page ?? 1),
          perPage: toInt(data?.perPage ?? data?.per_page, filters.perPage ?? 20),
        };
      },
      providesTags: (result) => [
        ...(result?.items ?? []).map((item) => cancellationTag(item.eventId)),
        cancellationListTag,
      ],
    }),

    getEventCancellationsSummary: build.query<
      EventCancellationsSummary,
      EventCancellationsFilters
    >({
      query: (filters) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.SUMMARY,
        params: cancellationsFilterParams(filters),
      }),
      transformResponse: (response: unknown) => {
        const data = (unwrap(response) ?? {}) as Record<string, unknown>;
        return {
          totalCancellations: toInt(data.totalCancellations ?? data.total_cancellations),
          topReasonText: toNullableString(data.topReasonText ?? data.top_reason_text),
          topReasonCount: toInt(data.topReasonCount ?? data.top_reason_count),
          affectedUsers: toInt(data.affectedUsers ?? data.affected_users),
        };
      },
      providesTags: [cancellationListTag],
    }),

    exportEventCancellations: build.mutation<
      { status: 200; blob: Blob; filename: string } | { status: 204 },
      EventCancellationsFilters
    >({
      query: (filters) => ({
        url: API_ENDPOINTS.EVENT_CANCELLATIONS.EXPORT,
        params: cancellationsFilterParams(filters),
        responseHandler: async (response) => {
          if (response.status === 204) return { status: 204 as const };

          const contentType = response.headers.get("Content-Type") || "";
          if (!response.ok) {
            return contentType.includes("json")
              ? ((await response.json()) as unknown)
              : { message: `Error al exportar (${response.status})` };
          }

          return {
            status: 200 as const,
            blob: await response.blob(),
            filename:
              parseContentDispositionFilename(
                response.headers.get("Content-Disposition")
              ) ?? DEFAULT_EXPORT_FILENAME,
          };
        },
        validateStatus: (response) => response.ok || response.status === 204,
      }),
    }),
  }),
});

export const {
  useGetCancellationReasonsQuery,
  useCreateCancellationReasonMutation,
  useUpdateCancellationReasonMutation,
  useDeleteCancellationReasonMutation,
  useReorderCancellationReasonsMutation,
  useResetCancellationReasonsMutation,
  useCancelEventMutation,
  useGetEventCancellationQuery,
  useGetAdminEventCancellationsQuery,
  useGetEventCancellationsSummaryQuery,
  useExportEventCancellationsMutation,
} = eventCancellationsApi;
